import { isRunningClocking } from '@components/common/sourceIcons'
import {
  isNoiseEntry,
  sumEntrySlotDuration,
} from '@components/common/timeNoiseUtils'

import { ROUTER_PATH } from '../../router/config/constants'
import type { TimelineSuggestedDurationFilter } from '../types'

const TIMELINE_DURATION_SESSION_PATHS = [
  ROUTER_PATH.dashboard,
  ROUTER_PATH.timeEntries,
]

export function isTimelineDurationSessionPath(pathname: string) {
  return TIMELINE_DURATION_SESSION_PATHS.includes(pathname)
}

export interface SuggestedDurationFilterOption {
  value: TimelineSuggestedDurationFilter
  label: string
  buttonLabel: string
  isTriangle?: boolean
  isUnknown?: boolean
  minSeconds?: number
}

export const DEFAULT_SUGGESTED_DURATION_FILTER: TimelineSuggestedDurationFilter =
  'all'
export const DEFAULT_TIME_VIEW_DURATION_FILTER: TimelineSuggestedDurationFilter =
  'gt_30s'

export function getDefaultSuggestedDurationFilter(workItemsMode: boolean) {
  return workItemsMode
    ? DEFAULT_TIME_VIEW_DURATION_FILTER
    : DEFAULT_SUGGESTED_DURATION_FILTER
}

let sessionSuggestedDurationFilter: TimelineSuggestedDurationFilter =
  DEFAULT_SUGGESTED_DURATION_FILTER

export function getSessionSuggestedDurationFilter() {
  return sessionSuggestedDurationFilter
}

export function setSessionSuggestedDurationFilter(
  value: TimelineSuggestedDurationFilter,
) {
  sessionSuggestedDurationFilter = isSuggestedDurationFilter(value)
    ? value
    : DEFAULT_SUGGESTED_DURATION_FILTER
}

export function resetSessionSuggestedDurationFilter() {
  sessionSuggestedDurationFilter = DEFAULT_SUGGESTED_DURATION_FILTER
}

export const SUGGESTED_DURATION_FILTER_OPTIONS: SuggestedDurationFilterOption[] =
  [
    { value: 'all', label: 'All Duration', buttonLabel: 'Duration' },
    {
      value: 'known',
      label: 'Exclude unknown duration',
      buttonLabel: 'Exclude unknown duration',
      isUnknown: false,
    },
    {
      value: 'unknown',
      label: 'Duration is unknown',
      buttonLabel: 'Duration is unknown',
      isUnknown: true,
    },
    {
      value: 'hidden_triangle',
      label: 'Exclude warning duration',
      buttonLabel: 'Exclude warning duration',
      isTriangle: false,
    },
    {
      value: 'show_triangle',
      label: 'Duration is warning',
      buttonLabel: 'Duration is warning',
      isTriangle: true,
    },
    {
      value: 'gt_10s',
      label: 'Duration > 10s',
      buttonLabel: 'Duration > 10s',
      minSeconds: 10,
    },
    {
      value: 'gt_30s',
      label: 'Duration > 30s',
      buttonLabel: 'Duration > 30s',
      minSeconds: 30,
    },
    {
      value: 'gt_1m',
      label: 'Duration > 1m',
      buttonLabel: 'Duration > 1m',
      minSeconds: 60,
    },
    {
      value: 'gt_2m',
      label: 'Duration > 2m',
      buttonLabel: 'Duration > 2m',
      minSeconds: 120,
    },
    {
      value: 'gt_6m',
      label: 'Duration > 6m (0.1h)',
      buttonLabel: 'Duration > 6m (0.1h)',
      minSeconds: 360,
    },
  ]

const SUGGESTED_DURATION_FILTER_VALUES = new Set<string>(
  SUGGESTED_DURATION_FILTER_OPTIONS.map(option => option.value),
)

export function isSuggestedDurationFilter(
  value: unknown,
): value is TimelineSuggestedDurationFilter {
  return (
    typeof value === 'string' && SUGGESTED_DURATION_FILTER_VALUES.has(value)
  )
}

export function getSuggestedDurationFilterOption(
  value: TimelineSuggestedDurationFilter,
) {
  return (
    SUGGESTED_DURATION_FILTER_OPTIONS.find(option => option.value === value) ??
    SUGGESTED_DURATION_FILTER_OPTIONS.find(
      option => option.value === DEFAULT_SUGGESTED_DURATION_FILTER,
    )
  )
}

export function getSuggestedEntryDurationSeconds(entry: unknown) {
  if (!entry || typeof entry !== 'object') return 0

  return isNoiseEntry(entry)
    ? sumEntrySlotDuration(entry)
    : sumEntrySlotDuration(entry, { excludeNoise: true })
}

function getSuggestedEntryDurationState(entry: unknown) {
  if (!entry || typeof entry !== 'object') {
    return { isTriangle: false, isUnknown: false }
  }

  const item = entry as {
    duration?: unknown
    showTriangle?: boolean
    slots?: Array<{ stop_display?: string | null }>
    stop?: unknown
    stop_display?: string | null
  }

  return {
    isTriangle: Boolean(item.showTriangle && item.stop !== null),
    isUnknown: Boolean(
      item.duration == null ||
        item.stop == null ||
        item.stop_display === null ||
        (item.slots?.length === 1 && !item.slots[0].stop_display)
    ),
  }
}

export function entryMatchesSuggestedDurationFilter(
  entry: unknown,
  filterValue: TimelineSuggestedDurationFilter | null | undefined,
) {
  if (!entry) return false
  if (!filterValue || filterValue === 'all') return true
  if (isRunningClocking(entry)) return true

  const option = getSuggestedDurationFilterOption(filterValue)
  if (!option) return true

  const durationState = getSuggestedEntryDurationState(entry)
  if (
    option.isUnknown != null &&
    durationState.isUnknown !== option.isUnknown
  ) {
    return false
  }
  if (
    option.isTriangle != null &&
    durationState.isTriangle !== option.isTriangle
  ) {
    return false
  }
  if (option.minSeconds == null) return true

  return getSuggestedEntryDurationSeconds(entry) > option.minSeconds
}
